import * as React from "react";
import { withTranslation } from "react-i18next";
import { StyledModal } from "./StyledModal";
import { DataService } from "../../services/DataService";
import i18n from "../../i18n";
import {
  InvalidRowColors,
  ProjectInvoice,
  ProjectInvoiceKeys,
} from "../../pages/KickbackProjectReportPage";
import EditableTable, { GroupedCol } from "../EditableTable";
import { ColumnDef } from "@tanstack/react-table";
import { Button, Spinner, Stack } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamation, faTrash } from "@fortawesome/free-solid-svg-icons";
import { Typeahead } from "react-bootstrap-typeahead";
import { _helpers, generateRgb } from "../../lib/ProjectReportValidation";
import { Invoice } from "../../services/API/Invoices";
import { getActiveSupplier } from "../../lib/utils";
interface ProjectInvoiceGrouped extends ProjectInvoice {
  items: ProjectInvoice[];
}

interface IKickbackreportDuplicatesProps {
  closeModal: () => void;
  period: string;
  report: ProjectInvoice[];
  invalidColors: InvalidRowColors;
  handleSubmitModal: (
    verifiedData?: ProjectInvoice[],
    deletedRows?: number[],
  ) => void;
  customerDict: any;
  contractDict: any;
  validateSum: (sumToValidate: string) => [boolean, string];
  csrd: boolean;
  editableColumns: ColumnDef<ProjectInvoice>[];
  groupedColumns: GroupedCol<ProjectInvoice>[];
  findDbDuplicates: (data: ProjectInvoice[]) => void;
}

interface IKickbackreportDuplicatesState {
  invoices: ProjectInvoice[];
  loading: boolean;
  width: number;
  keyedInvoices: Record<string, Invoice[]>;
  deletedRows: number[];
  collapsedGroups: number[];
}

class KickbackReportDuplicatesModal extends React.Component<
  IKickbackreportDuplicatesProps,
  IKickbackreportDuplicatesState
> {
  constructor(props: IKickbackreportDuplicatesProps) {
    super(props);
    this.state = {
      invoices: [],
      loading: true,
      width: window.innerWidth,
      keyedInvoices: {},
      deletedRows: [],
      collapsedGroups: [],
    };
  }

  duplicateColumns: ColumnDef<ProjectInvoice>[] = [
    {
      id: "customerName",
      header: `${i18n.t("kickbackProject.customerName")}`,
      cell: (props) => this._cell(props, "customerName"),
      minSize: 125,
    },
    {
      id: "customerOrgId",
      header: `${i18n.t("kickbackProject.customerOrgId")}`,
      cell: (props) => this._cell(props, "customerOrgId"),
      minSize: 125,
    },
    {
      id: "contractId",
      header: `${i18n.t("kickbackProject.contractId")}`,
      cell: (props) => this._cell(props, "contractId"),
      minSize: 125,
    },
    {
      id: "totalExclMoms",
      header: `${i18n.t("kickbackProject.sum")}`,
      cell: (props) => this._cell(props, "totalExclMoms"),
      minSize: 100,
    },
    {
      id: "invoiceNumber",
      header: `${i18n.t("kickbackProject.invoiceNumber")}`,
      cell: (props) => this._cell(props, "invoiceNumber"),
      minSize: 125,
    },
    {
      id: "billingDate",
      header: `${i18n.t("kickbackProject.billingDate")}`,
      cell: (props) => this._cell(props, "billingDate"),
      minSize: 40,
    },
    {
      accessorKey: "none",
      header: () => i18n.t("invoiceReport.table.headers.tools"),
      cell: (props) => {
        return (
          <Stack
            direction="horizontal"
            style={{
              height: 40,
              justifyContent: "space-evenly",
            }}
          >
            <Button
              className="button outline-button-danger row-btn"
              onClick={() => {
                this.handleRowDelete(props.row.original);
              }}
            >
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          </Stack>
        );
      },
    },
  ];

  private normalizeBillingDate = (value: unknown): string => {
    if (!value) return "";
    return value.toString().split("T")[0].trim();
  };

  private normalizeAmount = (value: unknown): number => {
    const candidate =
      typeof value === "object" && value !== null
        ? ((value as any).parsedValue ?? (value as any).source ?? 0)
        : value;

    if (typeof candidate === "number") {
      return Number(candidate.toFixed(2));
    }

    const parsed = parseFloat(
      String(candidate ?? "")
        .replace(/\s/g, "")
        .replace(",", "."),
    );

    if (Number.isNaN(parsed)) return 0;
    return Number(parsed.toFixed(2));
  };

  private getInvoiceSupplierOrgId = (item: any): string => {
    return String(item.supplierOrgId ?? item.SupplierOrgId ?? "").trim();
  };

  private getInvoiceContractId = (item: any): string => {
    return String(item.contractId ?? item.ContractId ?? "");
  };

  private getInvoiceNumber = (item: any): string => {
    return String(
      item.supplierInvoiceNumber ??
        item.SupplierInvoiceNumber ??
        item.invoiceNumber ??
        item.InvoiceNumber ??
        "",
    ).trim();
  };

  private getInvoiceBillingDate = (item: any): string => {
    return String(item.billingDate ?? item.BillingDate ?? "");
  };

  private getInvoiceAmount = (item: any): number => {
    return this.normalizeAmount(
      item.priceExcMoms ??
        item.PriceExcMoms ??
        item.sumExclMoms ??
        item.SumExclMoms ??
        0,
    );
  };

  private getProjectSupplierOrgId = (item: ProjectInvoice): string => {
    const supplierOrgId = String(
      (item as any).supplierOrgId ?? (item as any).SupplierOrgId ?? "",
    ).trim();
    console.log(
      supplierOrgId.length > 0
        ? supplierOrgId
        : String(getActiveSupplier() ?? "").trim(),
    );
    return supplierOrgId.length > 0
      ? supplierOrgId
      : String(getActiveSupplier() ?? "").trim();
  };

  private buildBaseKey = (
    supplierOrgId: string,
    contractId: string,
    invoiceNumber: string,
  ): string => {
    return [
      String(supplierOrgId ?? ""),
      String(invoiceNumber ?? "").trim(),
      String(contractId ?? ""),
    ].join("#");
  };

  private buildInvoiceBaseKey = (item: Invoice): string => {
    return this.buildBaseKey(
      this.getInvoiceSupplierOrgId(item),
      this.getInvoiceContractId(item),
      this.getInvoiceNumber(item),
    );
  };

  private buildProjectInvoiceBaseKey = (item: ProjectInvoice): string => {
    return this.buildBaseKey(
      this.getProjectSupplierOrgId(item),
      String(item.contractId ?? ""),
      String(item.invoiceNumber ?? "").trim(),
    );
  };

  private groupInvoicesByBaseKey = (
    data: Invoice[],
  ): Record<string, Invoice[]> => {
    return data.reduce((acc: Record<string, Invoice[]>, item) => {
      const key = this.buildInvoiceBaseKey(item);
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);
      return acc;
    }, {});
  };

  private isDuplicateByClientRule = (
    supplierOrgId: string,
    contractId: string,
    invoiceNumber: string,
    billingDate: string,
    amount: number,
    keyedInvoices: Record<string, Invoice[]>,
  ): boolean => {
    const baseKey = this.buildBaseKey(
      String(supplierOrgId ?? ""),
      String(contractId ?? ""),
      String(invoiceNumber ?? ""),
    );

    const existingInvoices = keyedInvoices[baseKey] ?? [];
    if (existingInvoices.length === 0) return false;

    const normalizedBillingDate = this.normalizeBillingDate(billingDate);
    const normalizedAmount = this.normalizeAmount(amount);

    return existingInvoices.some((existingInvoice) => {
      const existingBillingDate = this.normalizeBillingDate(
        this.getInvoiceBillingDate(existingInvoice),
      );

      const existingAmount = this.getInvoiceAmount(existingInvoice);

      const sameDate = existingBillingDate === normalizedBillingDate;
      const sameAmount = existingAmount === normalizedAmount;

      // Tillåt ENDAST: samma datum + annat belopp
      // Stoppa allt annat
      return !(sameDate && !sameAmount);
    });
  };

  private getAmountForDuplicateCheck = (item: ProjectInvoice): number => {
    if (this.props.csrd) {
      return this.normalizeAmount(item.rowSumExclMoms);
    }

    return this.normalizeAmount(item.totalExclMoms.value);
  };

  private recalculateDuplicateMarkers = (
    invoices: ProjectInvoice[],
  ): ProjectInvoice[] => {
    const colorByBaseKey: Record<string, string> = {};

    return invoices.map((invoice) => {
      const isDuplicate = this.isDuplicateByClientRule(
        this.getProjectSupplierOrgId(invoice),
        String(invoice.contractId ?? ""),
        String(invoice.invoiceNumber ?? ""),
        String(invoice.billingDate ?? ""),
        this.getAmountForDuplicateCheck(invoice),
        this.state.keyedInvoices,
      );

      if (!isDuplicate) {
        return {
          ...invoice,
          isDuplicate: {
            value: false,
            backgorundColor: "",
          },
        };
      }

      const baseKey = this.buildProjectInvoiceBaseKey(invoice);

      if (!colorByBaseKey[baseKey]) {
        colorByBaseKey[baseKey] =
          invoice.isDuplicate.backgorundColor || generateRgb();
      }

      return {
        ...invoice,
        isDuplicate: {
          value: true,
          backgorundColor: colorByBaseKey[baseKey],
        },
      };
    });
  };

  async componentDidMount(): Promise<void> {
    const data = await DataService.Invoices.GetProjectReportData(
      this.props.period,
      i18n.language,
    );
    console.log(data, "DATABASHÖMTNING", this.props.csrd);
    if (this.props.csrd && data.length > 0) {
      const keyedInvoices = this.groupInvoicesByBaseKey(data);

      const groupedAndSummedCsrdRows = Object.values(
        this.props.report.reduce(
          (
            acc: { [key: string]: { originalGroup: ProjectInvoice[] } },
            item,
          ) => {
            if (!acc[`${item.groupId}`]) {
              acc[`${item.groupId}`] = { originalGroup: [] };
            }
            acc[`${item.groupId}`].originalGroup.push(item);
            return acc;
          },
          {},
        ),
      ).map((inv) => {
        return inv.originalGroup.reduce(
          (
            acc: {
              csrdGroup: ProjectInvoice[];
              supplierOrgId: string;
              customerOrgId: string;
              contractId: string;
              invoiceNumber: string;
              billingDate: string;
              priceExcMoms: number;
            },
            item,
          ) => {
            acc.supplierOrgId = this.getProjectSupplierOrgId(item);
            acc.customerOrgId = item.customerOrgId;
            acc.contractId = item.contractId;
            acc.invoiceNumber = acc.invoiceNumber || item.invoiceNumber || "";
            acc.billingDate =
              acc.billingDate ||
              (item.billingDate ? item.billingDate.split("T")[0] : "");
            acc.priceExcMoms += this.normalizeAmount(item.rowSumExclMoms);
            return acc;
          },
          {
            csrdGroup: inv.originalGroup,
            supplierOrgId: "",
            customerOrgId: "",
            contractId: "",
            invoiceNumber: "",
            billingDate: "",
            priceExcMoms: 0,
          },
        );
      });

      const res: ProjectInvoice[] = [];

      groupedAndSummedCsrdRows.forEach((groupedRow) => {
        const isDuplicate = this.isDuplicateByClientRule(
          groupedRow.supplierOrgId,
          groupedRow.contractId,
          groupedRow.invoiceNumber,
          groupedRow.billingDate,
          groupedRow.priceExcMoms,
          keyedInvoices,
        );

        if (isDuplicate) {
          const dupColor = generateRgb();
          groupedRow.csrdGroup.forEach((x: ProjectInvoice) =>
            res.push({
              ...x,
              isDuplicate: { value: true, backgorundColor: dupColor },
            }),
          );
        }
      });

      if (res.length > 0) {
        this.props.findDbDuplicates(res);
        window.addEventListener("resize", this.handleResize);
        this.setState({ invoices: res, keyedInvoices }, () =>
          this.setState({ loading: false }),
        );
      } else {
        this.props.findDbDuplicates([]);
        this.props.handleSubmitModal();
      }
    } else {
      if (data.length > 0) {
        const keyedInvoices = this.groupInvoicesByBaseKey(data);

        let counter = 1;
        const duplicates: ProjectInvoice[] = this.props.report.reduce(
          (duplicates: ProjectInvoice[], item) => {
            const isDuplicate = this.isDuplicateByClientRule(
              this.getProjectSupplierOrgId(item),
              String(item.contractId ?? ""),
              String(item.invoiceNumber ?? ""),
              String(item.billingDate ?? ""),
              this.normalizeAmount(item.totalExclMoms.value),
              keyedInvoices,
            );

            if (isDuplicate) {
              const dup = JSON.parse(JSON.stringify(item));
              dup.isDuplicate = {
                value: true,
                backgorundColor:
                  this.props.invalidColors[counter] ?? generateRgb(),
              };
              duplicates.push(dup);
              counter++;
            }

            return duplicates;
          },
          [],
        );

        if (duplicates.length > 0) {
          this.props.findDbDuplicates(duplicates);
          window.addEventListener("resize", this.handleResize);
          this.setState({ invoices: duplicates, keyedInvoices }, () =>
            this.setState({ loading: false }),
          );
        } else {
          this.props.findDbDuplicates([]);
          this.props.handleSubmitModal();
        }
      } else {
        this.props.findDbDuplicates([]);
        this.props.handleSubmitModal();
      }
    }
  }

  async componentWillUnmount(): Promise<void> {
    window.removeEventListener("resize", this.handleResize);
  }

  private handleResize = (): void => {
    this.setState({ width: window.innerWidth });
  };

  private verifyChanges = (): void => {
    const recalculated = this.recalculateDuplicateMarkers(this.state.invoices);

    const remainingDuplicates = recalculated.filter(
      (invoice) => invoice.isDuplicate.value,
    );

    if (remainingDuplicates.length === 0) {
      this.props.findDbDuplicates([]);
      this.props.handleSubmitModal(recalculated, this.state.deletedRows);
      return;
    }

    this.props.findDbDuplicates(remainingDuplicates);
    this.setState({ invoices: recalculated });
  };

  private handleRowDelete = (row: ProjectInvoice): void => {
    const res = this.state.invoices.filter(
      (x) => x.uniqueRowId !== row.uniqueRowId,
    );

    const recalculated = this.recalculateDuplicateMarkers(res);
    const remainingDuplicates = recalculated.filter(
      (invoice) => invoice.isDuplicate.value,
    );

    this.props.findDbDuplicates(remainingDuplicates);

    this.setState((prevState) => ({
      invoices: recalculated,
      deletedRows: [...prevState.deletedRows, row.uniqueRowId],
    }));
  };

  private onChangeDuplicates = (
    change: any,
    key: string,
    rowId: number,
  ): void => {
    const updated = this.update(change, key, rowId);
    const recalculated = this.recalculateDuplicateMarkers(updated);
    const remainingDuplicates = recalculated.filter(
      (invoice) => invoice.isDuplicate.value,
    );

    this.props.findDbDuplicates(remainingDuplicates);

    this.setState({ invoices: recalculated });
  };

  private update = (
    change: any,
    key: string,
    rowId: number,
  ): ProjectInvoice[] => {
    let stateAsOfNow: ProjectInvoice[] = _helpers.deepClone(
      this.state.invoices,
    );

    for (let index = 0; index < stateAsOfNow.length; index++) {
      if (stateAsOfNow[index].uniqueRowId == rowId) {
        switch (key) {
          case "totalExclMoms": {
            stateAsOfNow[index][key]["value"] = +change;
            stateAsOfNow[index][key]["display"] = change;
            stateAsOfNow[index][key]["valid"] =
              this.props.validateSum(change)[0];
            break;
          }
          case "invoiceNumber": {
            stateAsOfNow[index][key] = String(change ?? "").trim();
            break;
          }
          case "billingDate": {
            stateAsOfNow[index][key] = change;
            break;
          }
          case "contractId": {
            stateAsOfNow[index][key] =
              typeof change[0] == "undefined" ? "" : change[0];
            break;
          }
          case "customerName": {
            stateAsOfNow[index]["customerName"] =
              typeof change[0] == "undefined" ? "" : change[0];
            stateAsOfNow[index]["customerOrgId"] =
              typeof change[0] == "undefined"
                ? ""
                : this.props.customerDict[change[0]]["customerOrgId"];
            break;
          }
          case "customerOrgId": {
            stateAsOfNow[index]["customerOrgId"] =
              typeof change[0] == "undefined" ? "" : change[0];
            stateAsOfNow[index]["customerName"] =
              typeof change[0] == "undefined"
                ? ""
                : Object.entries(this.props.customerDict).filter(
                    ([key, value]) => {
                      const buff: any = value;
                      if (buff["customerOrgId"] == change[0]) {
                        return key;
                      }
                    },
                  )[0][0];
            break;
          }
        }
      }
    }

    return stateAsOfNow;
  };

  private _cell = (
    props: any,
    key: ProjectInvoiceKeys,
  ): JSX.Element | undefined => {
    const item = props.row.original;

    if (key == "totalExclMoms") {
      return (
        <div
          className={`fieldWrapper ${
            item.totalExclMoms.display.length == 0
              ? "fieldWrapper-error"
              : item.totalExclMoms.valid
                ? ""
                : "fieldWrapper-warn"
          }`}
          style={
            item.isDuplicate.value == true
              ? { backgroundColor: `${item.isDuplicate.backgorundColor}` }
              : { backgroundColor: "" }
          }
        >
          <input
            className="numberFieldTest"
            placeholder={i18n.t("kickbackProject.fieldSumInput") ?? ""}
            style={{ width: "100%", appearance: "none" }}
            type="number"
            inputMode="numeric"
            onChange={(e) =>
              this.onChangeDuplicates(e.target.value, key, item.uniqueRowId)
            }
            value={item.totalExclMoms.display}
          />
          {!item.totalExclMoms.valid && (
            <FontAwesomeIcon
              className="exclamationIconStyle"
              style={{ zIndex: 100 }}
              icon={faExclamation}
            />
          )}
        </div>
      );
    } else if (key == "invoiceNumber") {
      return (
        <div
          className={`fieldWrapper ${
            item.invoiceNumber.length == 0 ? "fieldWrapper-error" : ""
          }`}
          style={
            item.isDuplicate.value == true
              ? { backgroundColor: `${item.isDuplicate.backgorundColor}` }
              : { backgroundColor: "" }
          }
        >
          <input
            placeholder={
              i18n.t("kickbackProject.fieldInvoiceNumberInput") ?? ""
            }
            style={{ width: "100%" }}
            type="text"
            onChange={(e) =>
              this.onChangeDuplicates(e.target.value, key, item.uniqueRowId)
            }
            value={item.invoiceNumber}
          />
          {item.invoiceNumber.length == 0 && (
            <FontAwesomeIcon
              className="exclamationIconStyle"
              icon={faExclamation}
            />
          )}
        </div>
      );
    } else if (key == "billingDate") {
      return (
        <div
          className={`fieldWrapper ${
            item.billingDate.length > 0 ? "" : "fieldWrapper-error"
          }`}
          style={
            item.isDuplicate.value == true
              ? { backgroundColor: `${item.isDuplicate.backgorundColor}` }
              : { backgroundColor: "" }
          }
        >
          <input
            value={item.billingDate}
            min={_helpers.getMinOrMaxDateRange("min", this.props.period)}
            max={_helpers.getMinOrMaxDateRange("max", this.props.period)}
            onChange={(e) =>
              this.onChangeDuplicates(e.target.value, key, item.uniqueRowId)
            }
            required
            pattern="\d{4}-\d{2}-\d{2}"
            style={{
              cursor: "pointer",
              paddingRight: "30px",
            }}
            type="date"
          />
        </div>
      );
    } else if (key == "contractId") {
      return (
        <div
          className={`fieldWrapper ${
            item.contractId.length > 0 ? "" : "fieldWrapper-error"
          }`}
          style={
            item.isDuplicate.value == true
              ? {
                  backgroundColor: `${item.isDuplicate.backgorundColor}`,
                  height: "",
                }
              : { backgroundColor: "", height: "" }
          }
        >
          {item.contractId.length == 0 ? (
            <>
              <Typeahead
                positionFixed
                placeholder={
                  i18n.t("kickbackProject.contractIdFieldValidation") ?? ""
                }
                newSelectionPrefix={i18n.t("kickbackProject.add")}
                emptyLabel={`${i18n.t("kickbackProject.emptyLabel")}`}
                id={`${item.uniqueRowId}`}
                options={Object.keys(this.props.contractDict)}
                onChange={(e) =>
                  this.onChangeDuplicates(e, key, item.uniqueRowId)
                }
              >
                {this.state.invoices.find(
                  (x) => x.uniqueRowId == item.uniqueRowId,
                )?.contractId ?? ""}
              </Typeahead>
              <FontAwesomeIcon
                style={{
                  color: "#fff",
                  fontSize: 20,
                  right: 0,
                  top: 20,
                  paddingBottom: 0,
                }}
                icon={faExclamation}
              />
            </>
          ) : (
            <div
              title={`${i18n.t("kickbackProject.changeContractLabel")}`}
              style={{
                cursor: "pointer",
                height: "100%",
                alignItems: "center",
                display: "flex",
                padding: "0.3em",
                position: "relative",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                overflow: "hidden",
                width: "125px",
              }}
              onClick={(e) => this.onChangeDuplicates(e, key, item.uniqueRowId)}
            >
              <span
                style={{
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                }}
              >
                {item.contractId}-
                {this.props.contractDict[item.contractId].title}-
                {this.props.contractDict[item.contractId].name}
              </span>
            </div>
          )}
        </div>
      );
    } else if (key == "customerName" || key == "customerOrgId") {
      return (
        <div
          className={`fieldWrapper ${
            item.customerOrgId.length > 0 ? "" : "fieldWrapper-error"
          }`}
          style={
            item.isDuplicate.value == true
              ? {
                  backgroundColor: `${item.isDuplicate.backgorundColor}`,
                  height: "",
                }
              : { backgroundColor: "", height: "" }
          }
        >
          {item.customerName.length == 0 || item.customerOrgId.length == 0 ? (
            <>
              <Typeahead
                positionFixed
                placeholder={
                  i18n.t("kickbackProject.contractIdFieldValidation") ?? ""
                }
                newSelectionPrefix={i18n.t("kickbackProject.add")}
                emptyLabel={`${i18n.t("kickbackProject.emptyLabel")}`}
                id={`${item.uniqueRowId}`}
                options={
                  key == "customerName"
                    ? Object.keys(this.props.customerDict)
                    : Object.values(this.props.customerDict).map(
                        (x: any) => x.customerOrgId,
                      )
                }
                onChange={(e) =>
                  this.onChangeDuplicates(e, key, item.uniqueRowId)
                }
              >
                {this.state.invoices.find(
                  (x) => x.uniqueRowId == item.uniqueRowId,
                )?.[key] ?? ""}
              </Typeahead>
              <FontAwesomeIcon
                style={{
                  color: "#fff",
                  fontSize: 20,
                  right: 0,
                  top: 20,
                  paddingBottom: 0,
                }}
                icon={faExclamation}
              />
            </>
          ) : (
            <div
              title={
                key == "customerName"
                  ? `${i18n.t("kickbackProject.changeCustomer")}`
                  : `${i18n.t("kickbackProject.changeCustomerOrgId")}`
              }
              style={{
                cursor: "pointer",
                height: "100%",
                alignItems: "center",
                display: "flex",
                padding: "0.3em",
                position: "relative",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                overflow: "hidden",
                width: "125px",
              }}
              onClick={(e) => this.onChangeDuplicates(e, key, item.uniqueRowId)}
            >
              {item[key]}
            </div>
          )}
        </div>
      );
    } else {
      return (
        <div
          className="fieldWrapper"
          style={
            item.isDuplicate.value == true
              ? {
                  backgroundColor: `${item.isDuplicate.backgorundColor}`,
                  cursor: "pointer",
                  alignItems: "center",
                  display: "flex",
                  padding: "0.3em",
                  position: "relative",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                }
              : {
                  backgroundColor: "",
                  cursor: "pointer",
                  height: "100%",
                  alignItems: "center",
                  display: "flex",
                  padding: "0.3em",
                  position: "relative",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  width: "125px",
                }
          }
        >
          {item[key]}
        </div>
      );
    }
  };

  private renderLoadingScreen = (): JSX.Element => {
    return (
      <div style={{ height: 200 }}>
        <div>{i18n.t("kickbackProject.verificationButton")}</div>
        <Spinner />
      </div>
    );
  };

  render() {
    const sendOrnotSend = this.state.invoices.filter(
      (item) => item.isDuplicate.value,
    );

    return (
      <>
        {this.state.loading ? (
          <StyledModal
            headerStyle={{ backgroundColor: "#173c53", color: "white" }}
            title={i18n.t("kickbackProject.modalTitle").toString()}
            body={<>{this.renderLoadingScreen()}</>}
            onDismiss={this.props.closeModal}
          />
        ) : (
          <StyledModal
            headerStyle={{ backgroundColor: "#173c53", color: "white" }}
            onDismiss={this.props.closeModal}
            title={i18n.t("kickbackProject.manageDuplicates") ?? ""}
            body={
              <>
                <div>
                  <div
                    style={{
                      backgroundColor: "#FFFDF9",
                      padding: "2rem",
                      width: "100%",
                      maxWidth: "28rem",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        width: "5rem",
                        height: "5rem",
                        backgroundColor: "rgb(201, 205, 170)",
                        borderRadius: "9999px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 1.5rem auto",
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 32 32"
                        width={32}
                        fill="#173c53"
                      >
                        <path
                          id="SVGRepo_iconCarrier"
                          d="m.682 9.431 14.847 8.085a1 1 0 0 0 .953.002l15.003-8.085a.999.999 0 0 0-.022-1.772L16.615.121a1 1 0 0 0-.902-.002L.711 7.659a1 1 0 0 0-.03 1.772zm15.479-7.297L28.853 8.58 16.01 15.501 3.317 8.589zM31.292 15.01l-2.968-1.507-2.142 1.155 2.5 1.27-12.842 6.921-12.694-6.912 2.666-1.34-2.136-1.164-3.135 1.575a1 1 0 0 0-.03 1.772l14.847 8.085a1 1 0 0 0 .953.002l15.003-8.085a.999.999 0 0 0-.021-1.772zm0 7-2.811-1.382-2.142 1.155 2.344 1.145-12.843 6.921-12.694-6.912 2.478-1.121-2.136-1.164-2.947 1.357a1 1 0 0 0-.03 1.772l14.847 8.085a1 1 0 0 0 .954.002l15.003-8.085a.999.999 0 0 0-.021-1.772z"
                        ></path>
                      </svg>
                    </div>

                    <h3
                      style={{
                        fontSize: "1.5rem",
                        lineHeight: "2rem",
                        fontFamily: "serif",
                        color: "#111827",
                        marginBottom: "0.75rem",
                      }}
                    >
                      {i18n.t("duplicatesmodal.whoops")}
                    </h3>

                    <p
                      style={{
                        color: "#4B5563",
                        marginBottom: "2rem",
                        lineHeight: 1.625,
                      }}
                    >
                      {i18n.t("duplicatesmodal.looks")}{" "}
                      <span
                        style={{
                          fontWeight: "bold",
                          color: "#173c53",
                        }}
                      >
                        {this.state.invoices.length}{" "}
                        {i18n.t("duplicatesmodal.count")}
                      </span>{" "}
                      {i18n.t("duplicatesmodal.color")}
                    </p>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.75rem",
                      }}
                    >
                      <button
                        onClick={this.props.closeModal}
                        style={{
                          width: "100%",
                          padding: "1rem 0",
                          backgroundColor: "#173c53",
                          color: "white",
                          borderRadius: "0.375rem",
                          fontWeight: "bold",
                          fontSize: "1.125rem",
                          border: "none",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                        }}
                      >
                        {i18n.t("duplicatesmodal.fix")}
                      </button>
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    maxHeight: "100vh",
                    minHeight: "10vh",
                    overflow: "scroll",
                  }}
                >
                  {!this.props.csrd && (
                    <EditableTable
                      onChange={() => console.log()}
                      columns={this.duplicateColumns}
                      data={this.state.invoices}
                    />
                  )}
                </div>
              </>
            }
            footer={
              <div>
                {!this.props.csrd && (
                  <Button
                    title={i18n.t("kickbackProject.confirmChangesButton") ?? ""}
                    onClick={this.verifyChanges}
                    className="verifyButton"
                    disabled={sendOrnotSend.length > 0}
                  >
                    <FontAwesomeIcon
                      style={{ marginRight: 4 }}
                      icon="circle-check"
                      size="lg"
                    />
                    {i18n.t("kickbackProject.confirmChangesButton")}
                  </Button>
                )}
              </div>
            }
          />
        )}
      </>
    );
  }
}

export default withTranslation()(KickbackReportDuplicatesModal);
